// ─────────────────────────────────────────────────────────────────────────────
// CasePowerPill (TopNav)
// Live indicator + interactive smart-charge control for the CrewZ case.
//
// Listens to:  window 'crewz-power-status' { casePercent, isCharging,
//                                             phonePercent?, smartChargeWindow?,
//                                             timestamp? }
//
// Emits:       window 'crewz-power-config' { startPercent, stopPercent,
//                                             fullChargeOverride, timestamp }
//              persisted in localStorage 'crewz_smart_charge_config'.
//
// Goal: never let the case OR the phone go flat while in use. The user opens
// the popover to adjust the smart-charge window (default 15-50 %) or flip the
// full-charge override on. The firmware reads the dispatched event and applies
// the policy. On critical-low conditions the popover surfaces a warning.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { Battery, BatteryLow, BatteryFull, BatteryWarning, Zap, AlertTriangle, Clock } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const STALE_MS = 10000;
const STORAGE_KEY = 'crewz_smart_charge_config';
const DEFAULT_CONFIG = { startPercent: 15, stopPercent: 50, fullChargeOverride: false };
const CRITICAL_PCT = 10;

// ── Runtime estimator ──────────────────────────────────────────────────────
// Min samples + min time span before we trust the estimate.
const ESTIMATOR_MIN_SAMPLES = 3;
const ESTIMATOR_MIN_SPAN_MS = 30 * 1000;
const ESTIMATOR_MAX_SAMPLES = 30;
const ESTIMATOR_MAX_AGE_MS = 10 * 60 * 1000;
// Slopes below this threshold (% per minute) are treated as "stable" / no signal.
const SLOPE_DEAD_BAND = 0.05;

/** Linear-regression slope of (timestamp, value) pairs in % per millisecond.
 *  Timestamps are normalised to `t - t0` before regression to avoid
 *  precision loss with epoch-millisecond magnitudes. */
function regressionSlope(points) {
  if (!points || points.length < 2) return 0;
  const t0 = points[0][0];
  const n = points.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (const [x, y] of points) {
    const nx = x - t0;
    sumX += nx; sumY += y; sumXY += nx * y; sumXX += nx * nx;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return 0;
  return (n * sumXY - sumX * sumY) / denom;
}

const formatDurationMins = (mins) => {
  const m = Math.max(0, Math.round(mins));
  if (m < 1) return '<1m';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
};

const formatClockTime = (date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const pickIcon = (pct) => {
  if (pct == null) return Battery;
  if (pct <= 15) return BatteryWarning;
  if (pct <= 35) return BatteryLow;
  if (pct >= 90) return BatteryFull;
  return Battery;
};

const pctTone = (pct) => {
  if (pct == null) return 'text-zinc-500 dark:text-zinc-400';
  if (pct <= 15) return 'text-rose-600 dark:text-rose-400';
  if (pct <= 35) return 'text-amber-600 dark:text-amber-400';
  return 'text-emerald-600 dark:text-emerald-400';
};

const readConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw);
    return {
      startPercent: typeof parsed.startPercent === 'number' ? parsed.startPercent : DEFAULT_CONFIG.startPercent,
      stopPercent: typeof parsed.stopPercent === 'number' ? parsed.stopPercent : DEFAULT_CONFIG.stopPercent,
      fullChargeOverride: !!parsed.fullChargeOverride,
    };
  } catch {
    return DEFAULT_CONFIG;
  }
};

export default function CasePowerPill() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(readConfig);
  const [draft, setDraft] = useState(config);
  const [estimate, setEstimate] = useState(null);
  const samplesRef = useRef([]);
  const staleTimerRef = useRef(null);

  // Subscribe to live power status.
  useEffect(() => {
    const onStatus = (event) => {
      const d = event.detail || {};
      if (typeof d.casePercent !== 'number') return;
      const ts = d.timestamp || Date.now();
      const casePercent = Math.max(0, Math.min(100, d.casePercent));
      const phonePercent = typeof d.phonePercent === 'number' ? d.phonePercent : null;
      const isCharging = !!d.isCharging;

      setStatus({
        casePercent,
        isCharging,
        phonePercent,
        smartChargeWindow: Array.isArray(d.smartChargeWindow) ? d.smartChargeWindow : null,
        timestamp: ts,
      });

      // Append to estimator ring buffer (trimmed by count and age).
      const buf = samplesRef.current;
      buf.push({ t: ts, c: casePercent, p: phonePercent, charging: isCharging });
      while (buf.length > ESTIMATOR_MAX_SAMPLES) buf.shift();
      const cutoff = Date.now() - ESTIMATOR_MAX_AGE_MS;
      while (buf.length > 0 && buf[0].t < cutoff) buf.shift();

      // Compute estimate if we have enough data.
      const span = buf.length >= 2 ? (buf[buf.length - 1].t - buf[0].t) : 0;
      if (buf.length >= ESTIMATOR_MIN_SAMPLES && span >= ESTIMATOR_MIN_SPAN_MS) {
        const caseSlopePerMs = regressionSlope(buf.map(s => [s.t, s.c]));
        const caseSlopePerMin = caseSlopePerMs * 60000;
        const phonePoints = buf.filter(s => s.p != null).map(s => [s.t, s.p]);
        const phoneSlopePerMin = phonePoints.length >= 2
          ? regressionSlope(phonePoints) * 60000
          : 0;
        setEstimate({ caseSlopePerMin, phoneSlopePerMin });
      } else {
        setEstimate(null);
      }

      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      staleTimerRef.current = setTimeout(() => {
        // Clear status AND drop the sample buffer so the runtime estimator
        // restarts cleanly on the next stream rather than mixing old samples.
        setStatus(null);
        setEstimate(null);
        samplesRef.current = [];
      }, STALE_MS);
    };
    window.addEventListener('crewz-power-status', onStatus);
    return () => {
      window.removeEventListener('crewz-power-status', onStatus);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    };
  }, []);

  // Sync the editable draft when the popover opens.
  useEffect(() => {
    if (open) setDraft(config);
  }, [open, config]);

  // Critical-low watchdog (one toast per session for case, one for phone).
  const lowToastedRef = useRef({ case: false, phone: false });
  useEffect(() => {
    if (!status) return;
    if (status.casePercent <= CRITICAL_PCT && !lowToastedRef.current.case) {
      lowToastedRef.current.case = true;
      toast.warning(`CrewZ case battery critically low (${status.casePercent}%). Phone charging is now blocked to protect the case.`);
    } else if (status.casePercent > CRITICAL_PCT + 5) {
      lowToastedRef.current.case = false;
    }
    if (status.phonePercent != null && status.phonePercent <= CRITICAL_PCT && !lowToastedRef.current.phone) {
      lowToastedRef.current.phone = true;
      toast.warning(`Phone battery critically low (${status.phonePercent}%). Enable Full Charge override from the case battery pill if you need topping up immediately.`);
    } else if (status.phonePercent != null && status.phonePercent > CRITICAL_PCT + 5) {
      lowToastedRef.current.phone = false;
    }
  }, [status]);

  const Icon = useMemo(() => pickIcon(status?.casePercent), [status]);
  const tone = useMemo(() => pctTone(status?.casePercent), [status]);

  const persistAndDispatch = (next) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    setConfig(next);
    window.dispatchEvent(new CustomEvent('crewz-power-config', {
      detail: { ...next, timestamp: Date.now() },
    }));
  };

  const handleSave = () => {
    let { startPercent, stopPercent, fullChargeOverride } = draft;
    // Guard: keep a sensible 5% gap and clamp to [5..95].
    startPercent = Math.max(5, Math.min(95, Math.round(startPercent)));
    stopPercent = Math.max(startPercent + 5, Math.min(100, Math.round(stopPercent)));
    persistAndDispatch({ startPercent, stopPercent, fullChargeOverride });
    toast.success(fullChargeOverride
      ? 'Full Charge override active — case will charge phone to 100%.'
      : `Smart charge window set to ${startPercent}–${stopPercent}%.`);
    setOpen(false);
  };

  const handleResetDefaults = () => {
    setDraft(DEFAULT_CONFIG);
  };

  if (!status) return null;

  const { casePercent, isCharging, phonePercent } = status;
  const caseLow = casePercent <= 20;
  const phoneLow = phonePercent != null && phonePercent <= 20;
  const titleParts = [
    `CrewZ case battery: ${casePercent}%`,
    isCharging ? 'Charging your phone' : 'Idle',
  ];
  if (phonePercent != null) titleParts.push(`Phone: ${phonePercent}%`);
  titleParts.push(config.fullChargeOverride ? 'Full charge override ON' : `Smart window: ${config.startPercent}–${config.stopPercent}%`);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-2 hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-zinc-100 dark:bg-zinc-700 ring-1 ring-zinc-900/5 dark:ring-white/5 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition active:scale-[0.98]"
          title={titleParts.join(' · ')}
          data-testid="case-power-pill"
          aria-label={`CrewZ case battery ${casePercent} percent${isCharging ? ', charging phone' : ''}. Tap to adjust smart charge.`}
        >
          <Icon className={`w-3.5 h-3.5 ${tone}`} data-testid="case-power-pill-icon" />
          <span
            className={`font-mono tabular-nums text-xs font-bold ${tone}`}
            data-testid="case-power-pill-percent"
          >
            {casePercent}%
          </span>
          {isCharging && (
            <Zap
              className="w-3 h-3 text-amber-500 animate-pulse"
              data-testid="case-power-pill-charging-icon"
              aria-hidden="true"
            />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 bg-white dark:bg-zinc-900 border-2 border-zinc-200 dark:border-zinc-700 shadow-xl"
        data-testid="case-power-popover"
      >
        <div className="p-4 space-y-4">
          {/* Header status */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Smart Charge
              </div>
              <div className="text-sm font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                Keep the boat alive
              </div>
            </div>
            <div className="flex items-center gap-3 font-mono tabular-nums text-xs">
              <span className={`px-2 py-1 rounded-md ${caseLow ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300'}`}>
                Case <strong>{casePercent}%</strong>
              </span>
              {phonePercent != null && (
                <span className={`px-2 py-1 rounded-md ${phoneLow ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300'}`}>
                  Phone <strong>{phonePercent}%</strong>
                </span>
              )}
            </div>
          </div>

          {(caseLow || phoneLow) && (
            <div
              className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
              data-testid="case-power-popover-low-warning"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-snug">
                {caseLow && phoneLow
                  ? 'Both case and phone are low. Consider ending the session or enabling Full Charge override only if you can dock soon.'
                  : caseLow
                    ? 'Case is low. To preserve the case, charging the phone is throttled until you finish the session.'
                    : 'Phone is low. Enable Full Charge override below to bypass the smart window.'}
              </p>
            </div>
          )}

          {/* Estimated runtime panel */}
          <div
            className="rounded-lg bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 p-2.5"
            data-testid="case-power-popover-runtime"
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <Clock className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Estimated Runtime
              </span>
            </div>
            {!estimate ? (
              <div
                className="text-[11px] text-zinc-500 dark:text-zinc-400"
                data-testid="case-power-popover-runtime-calculating"
              >
                Calculating runtime…
              </div>
            ) : (
              <div className="space-y-1 text-[11px]">
                {/* Case ETA line */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-zinc-500 dark:text-zinc-400">Case</span>
                  {estimate.caseSlopePerMin <= -SLOPE_DEAD_BAND ? (
                    <span
                      className="font-mono tabular-nums font-bold text-zinc-900 dark:text-white"
                      data-testid="case-power-popover-runtime-case-eta"
                    >
                      ~{formatDurationMins(casePercent / -estimate.caseSlopePerMin)}
                      <span className="ml-1 font-sans font-normal text-zinc-500 dark:text-zinc-400">until depleted</span>
                    </span>
                  ) : estimate.caseSlopePerMin >= SLOPE_DEAD_BAND ? (
                    <span
                      className="text-emerald-600 dark:text-emerald-400 font-bold"
                      data-testid="case-power-popover-runtime-case-charging"
                    >
                      Charging
                    </span>
                  ) : (
                    <span
                      className="text-zinc-500 dark:text-zinc-400"
                      data-testid="case-power-popover-runtime-case-stable"
                    >
                      Stable draw
                    </span>
                  )}
                </div>

                {/* Phone ETA line — only when we have phone data + a meaningful slope */}
                {phonePercent != null && (() => {
                  const target = config.fullChargeOverride
                    ? 100
                    : Math.max(config.stopPercent, phonePercent);
                  const remaining = target - phonePercent;
                  if (estimate.phoneSlopePerMin >= SLOPE_DEAD_BAND && remaining > 0) {
                    const minsToTarget = remaining / estimate.phoneSlopePerMin;
                    const eta = new Date(Date.now() + minsToTarget * 60000);
                    return (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-zinc-500 dark:text-zinc-400">
                          Phone {target}%
                        </span>
                        <span
                          className="font-mono tabular-nums font-bold text-zinc-900 dark:text-white"
                          data-testid="case-power-popover-runtime-phone-eta"
                        >
                          ~{formatClockTime(eta)}
                          <span className="ml-1 font-sans font-normal text-zinc-500 dark:text-zinc-400">
                            ({formatDurationMins(minsToTarget)})
                          </span>
                        </span>
                      </div>
                    );
                  }
                  if (phonePercent >= target) {
                    return (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-zinc-500 dark:text-zinc-400">Phone</span>
                        <span
                          className="text-emerald-600 dark:text-emerald-400 font-bold"
                          data-testid="case-power-popover-runtime-phone-target-reached"
                        >
                          At target
                        </span>
                      </div>
                    );
                  }
                  if (estimate.phoneSlopePerMin <= -SLOPE_DEAD_BAND) {
                    const minsTo10 = (phonePercent - 10) / -estimate.phoneSlopePerMin;
                    if (minsTo10 > 0) {
                      return (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-zinc-500 dark:text-zinc-400">Phone</span>
                          <span
                            className="font-mono tabular-nums font-bold text-zinc-900 dark:text-white"
                            data-testid="case-power-popover-runtime-phone-discharging"
                          >
                            ~{formatDurationMins(minsTo10)}
                            <span className="ml-1 font-sans font-normal text-zinc-500 dark:text-zinc-400">to 10%</span>
                          </span>
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>
            )}
          </div>

          {/* Window range slider (dual-thumb) */}
          <div className={draft.fullChargeOverride ? 'opacity-50 pointer-events-none' : ''}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Smart window
              </span>
              <span className="font-mono tabular-nums text-xs font-bold text-zinc-900 dark:text-white">
                {draft.startPercent}% → {draft.stopPercent}%
              </span>
            </div>
            <SliderPrimitive.Root
              className="relative flex w-full touch-none select-none items-center my-3"
              min={5}
              max={100}
              step={1}
              minStepsBetweenThumbs={5}
              value={[draft.startPercent, draft.stopPercent]}
              onValueChange={([s, e]) => setDraft(d => ({ ...d, startPercent: s, stopPercent: e }))}
              data-testid="case-power-popover-window-slider"
              aria-label="Smart charge window"
              disabled={draft.fullChargeOverride}
            >
              <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                <SliderPrimitive.Range className="absolute h-full bg-emerald-500 dark:bg-emerald-400" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb
                className="block h-4 w-4 rounded-full border-2 border-emerald-600 dark:border-emerald-400 bg-white dark:bg-zinc-900 shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                data-testid="case-power-popover-window-thumb-start"
                aria-label="Start charging at"
              />
              <SliderPrimitive.Thumb
                className="block h-4 w-4 rounded-full border-2 border-emerald-600 dark:border-emerald-400 bg-white dark:bg-zinc-900 shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                data-testid="case-power-popover-window-thumb-stop"
                aria-label="Stop charging at"
              />
            </SliderPrimitive.Root>
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
              <span>Start at {draft.startPercent}%</span>
              <span>Stop at {draft.stopPercent}%</span>
            </div>
          </div>

          {/* Full charge override */}
          <label
            className="flex items-start gap-3 p-2.5 rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 cursor-pointer"
            data-testid="case-power-popover-override-row"
          >
            <Switch
              checked={draft.fullChargeOverride}
              onCheckedChange={(v) => setDraft(d => ({ ...d, fullChargeOverride: !!v }))}
              data-testid="case-power-popover-override-switch"
            />
            <span className="flex-1">
              <span className="block text-xs font-bold text-zinc-900 dark:text-white">
                Full Charge Override
              </span>
              <span className="block text-[11px] text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">
                Charge the phone to 100% regardless of the case window. Use only when you can dock soon.
              </span>
            </span>
          </label>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={handleResetDefaults}
              className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition"
              data-testid="case-power-popover-reset"
            >
              Reset Defaults
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                data-testid="case-power-popover-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:opacity-90 transition"
                data-testid="case-power-popover-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
